import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetCollaboratorQuery, usePatchCollaboratorMutation, useLazyGetProfileQuery } from "../redux/appSlices/collaborator/collaboratorApiSlice";
import { useGenerateWalletPassMutation } from "../redux/appSlices/generalApiSlice";
import MainCard from "../componants/profile/MainCard";
import { useDispatch, useSelector } from "react-redux";
import { getCollaborator } from "../redux/appSlices/collaborator/collaboratorSlice";
import { apiSlice } from "../redux/api/apiSlice";
import Loading from "../componants/Loading";
import ShowAbout from "../componants/profile/showSections/ShowAbout";
import ShowInformation from "../componants/profile/showSections/ShowInformation";
import ShowSocials from "../componants/profile/showSections/ShowSocials";
import ShowProducts from "../componants/profile/showSections/ShowProducts";
import ShowCalendly from "../componants/profile/showSections/ShowCalendly";
import ShowLinks from "../componants/profile/showSections/ShowLinks";
import ShowImages from "../componants/profile/showSections/ShowImages";
import ShowVideos from "../componants/profile/showSections/ShowVideos";
import Footer from "../componants/profile/Footer";
import useLoggedStatus from "../hooks/useLoggedStatus";
import AddToContact from "../componants/profile/AddToContactButton";
import AddToContactButton from "../componants/profile/AddToContactButton";
import TestShowImages from "../componants/profile/showSections/TestShowImages";
import Header from "../componants/profile/header/Header";
import LogoutAnimation from "../componants/LogoutAnimation";
import AddToContactButtonFloat from "../componants/profile/AddToContactButtonFloat";
import useTracking from "../hooks/useTracking";
import { profileRedirectMap } from "../constants";
import { translation } from "../translation";
import MeetingsModal from "../componants/profile/MeetingsModal";
import ProfileSwitcherBar from "../componants/profile/ProfileSwitcherBar";
import ProfileSwitchOverlay from "../componants/UI/ProfileSwitchOverlay";
import {
  buildMainProfilePreview,
  getProfileEntryId,
  isMainProfileId,
  normalizePublicProfilesList,
  withCollaboratorMeta,
} from "../utils/profilePicture";

/** Query flag so Mes rencontres survives refresh when logged in (e.g. ?view=mes-rencontres). */
const MEETINGS_VIEW_QUERY_KEY = "view";
const MEETINGS_VIEW_QUERY_VALUE = "mes-rencontres";

const getPublicProfileStorageKey = (collaboratorId) =>
  `publicActiveProfile_${collaboratorId}`;

const getStoredPublicProfileId = (collaboratorId) =>
  localStorage.getItem(getPublicProfileStorageKey(collaboratorId));

const setStoredPublicProfileId = (collaboratorId, profileId) => {
  localStorage.setItem(getPublicProfileStorageKey(collaboratorId), profileId);
};

const getSecondPublicProfile = (collaboratorData) => {
  const profiles = collaboratorData?.profiles || [];
  if (profiles.length < 2) return null;
  return profiles[1];
};

const getPublicProfilesList = (collaboratorData) =>
  normalizePublicProfilesList(collaboratorData);

const shouldShowPublicProfileSwitcher = (collaboratorData, activePublicProfile) => {
  if (!collaboratorData || !activePublicProfile) return false;

  return (
    collaboratorData.isMultiProfile === true &&
    collaboratorData.profileDisplayMode === "both" &&
    collaboratorData.profiles?.length >= 2
  );
};

const isCollaboratorPublicProfile = (profileId, collaboratorData) =>
  isMainProfileId(collaboratorData, profileId);

const buildCollaboratorState = (raw) => {
  if (raw.currentProfile && typeof raw.currentProfile === "object") {
    return withCollaboratorMeta(raw, raw.currentProfile);
  }

  if (raw.currentProfile) {
    return withCollaboratorMeta(raw, { currentProfile: raw.currentProfile });
  }

  return withCollaboratorMeta(raw);
};

const buildCollaboratorPublicProfile = (collaboratorData) =>
  buildMainProfilePreview(collaboratorData);

// import usePwaNavigation from "../hooks/usePwaNavigation";
const MIN_PROFILE_SWITCH_OVERLAY_MS = 550;

const Profile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const collaborator = useSelector(
    (state) => state.collaborator.collaboratorInfo,
  );
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );

  const logged = useLoggedStatus();
  // usePwaNavigation();
  const [hideLogout, setLogout] = useState(
    localStorage.getItem("logout") === "true" ? true : false,
  );
  const [activePublicProfile, setActivePublicProfile] = useState(null);
  const [contactButtonsVisible, setContactButtonsVisible] = useState(false);
  const [profileInitialized, setProfileInitialized] = useState(false);
  const [isSwitchingProfile, setIsSwitchingProfile] = useState(false);
  const profileSwitchStartedAtRef = useRef(0);

  const finishProfileSwitchOverlay = useCallback((callback) => {
    const elapsed = Date.now() - profileSwitchStartedAtRef.current;
    const remaining = Math.max(0, MIN_PROFILE_SWITCH_OVERLAY_MS - elapsed);
    window.setTimeout(callback, remaining);
  }, []);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const profileInitStartedRef = useRef(false);
  
  // Store the original collaborator data to avoid infinite loops
  const originalCollaboratorRef = useRef(null);
  
  const { sendTracking } = useTracking();
  const [generateWalletPass] = useGenerateWalletPassMutation();

  const { data, isLoading } = useGetCollaboratorQuery(id);
  const [fetchProfile] = useLazyGetProfileQuery();

  const applyCollaboratorProfile = useCallback(() => {
    const base = originalCollaboratorRef.current;
    if (!base) return;

    const profilePreview = buildCollaboratorPublicProfile(base);
    setStoredPublicProfileId(base.collaborator_id, base._id);
    setActivePublicProfile(profilePreview);
    dispatch(getCollaborator(withCollaboratorMeta(base)));
  }, [dispatch]);

  const applyPublicProfile = useCallback(
    (profile) => {
      const base = originalCollaboratorRef.current;
      if (!base || !profile) return;

      setStoredPublicProfileId(base.collaborator_id, profile._id);
      setActivePublicProfile(profile);
      dispatch(getCollaborator(withCollaboratorMeta(base, profile)));
    },
    [dispatch],
  );

  const loadPublicProfile = useCallback(
    async (profileId, { showOverlay = false } = {}) => {
      const base = originalCollaboratorRef.current;
      if (base && isCollaboratorPublicProfile(profileId, base)) {
        applyCollaboratorProfile();
        return;
      }

      if (showOverlay) {
        setIsSwitchingProfile(true);
        profileSwitchStartedAtRef.current = Date.now();
      }

      try {
        const result = await fetchProfile(profileId, false).unwrap();
        applyPublicProfile(result?.data?.profile);
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        if (showOverlay) {
          finishProfileSwitchOverlay(() => setIsSwitchingProfile(false));
        }
      }
    },
    [fetchProfile, applyPublicProfile, applyCollaboratorProfile, finishProfileSwitchOverlay],
  );

  const [patchCollaborator] = usePatchCollaboratorMutation();

  const storedCollaboratorId = localStorage.getItem("collaborator_id");
  const profileCollaboratorId = data?.data?.collaborator?.collaborator_id;
  /** Same rule as useLoggedStatus but uses API payload so refresh works before Redux catches up. */
  const canUseMeetingsFeature =
    !!localStorage.getItem("token") &&
    !!storedCollaboratorId &&
    !!profileCollaboratorId &&
    String(profileCollaboratorId) === String(storedCollaboratorId);

  const showMeetingsModal =
    searchParams.get(MEETINGS_VIEW_QUERY_KEY) === MEETINGS_VIEW_QUERY_VALUE &&
    canUseMeetingsFeature;

  const openMeetingsModal = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(MEETINGS_VIEW_QUERY_KEY, MEETINGS_VIEW_QUERY_VALUE);
        return next;
      },
      { replace: false },
    );
  };

  const closeMeetingsModal = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(MEETINGS_VIEW_QUERY_KEY);
        return next;
      },
      { replace: true },
    );
  };

  // Reset profile state when viewing a different collaborator
  useEffect(() => {
    profileInitStartedRef.current = false;
    setProfileInitialized(false);
    setActivePublicProfile(null);
    setIsSwitchingProfile(false);
  }, [id]);

  // Initialize public profile: default to first created profile, restore from storage on refresh
  useEffect(() => {
    if (logged || profileInitialized || profileInitStartedRef.current) return;

    const base = originalCollaboratorRef.current;
    if (
      !base ||
      !base.isMultiProfile ||
      base.profileDisplayMode !== "both" ||
      base.profiles?.length < 2
    ) {
      return;
    }

    profileInitStartedRef.current = true;

    const secondProfile = getSecondPublicProfile(base);
    const secondProfileId = getProfileEntryId(secondProfile);
    const storedId = getStoredPublicProfileId(base.collaborator_id);

    const initProfile = async () => {
      if (secondProfileId && storedId === secondProfileId) {
        await loadPublicProfile(secondProfileId, { showOverlay: false });
      } else {
        applyCollaboratorProfile();
      }
      setProfileInitialized(true);
    };

    initProfile();
  }, [
    logged,
    profileInitialized,
    data,
    loadPublicProfile,
    applyCollaboratorProfile,
  ]);

  useEffect(() => {
    if (logged) {
      setIsSwitchingProfile(false);
      setContactButtonsVisible(false);
    }
  }, [logged]);

  // Lift switcher above footer buttons only when scrolled to the bottom (public visitors)
  useEffect(() => {
    if (logged) return undefined;

    const handleScroll = () => {
      try {
        const showBtn = document.getElementById("show-btn");
        if (!showBtn) return;

        const showBtnRect = showBtn.getBoundingClientRect();
        setContactButtonsVisible(showBtnRect.y + 60 < window.scrollY);
      } catch {
        // ignore
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [logged]);

  const handleSelectProfile = (profileId, { showOverlay = false } = {}) => {
    if (!logged && originalCollaboratorRef.current) {
      const base = originalCollaboratorRef.current;
      const publicProfiles = getPublicProfilesList(base);

      if (activePublicProfile?._id === profileId) {
        return;
      }

      if (!publicProfiles.some(
        (profile) => String(getProfileEntryId(profile)) === String(profileId),
      )) {
        return;
      }

      dispatch(apiSlice.util.invalidateTags(["profile"]));

      if (isCollaboratorPublicProfile(profileId, base)) {
        if (showOverlay) {
          setIsSwitchingProfile(true);
          profileSwitchStartedAtRef.current = Date.now();
          applyCollaboratorProfile();
          finishProfileSwitchOverlay(() => setIsSwitchingProfile(false));
        } else {
          applyCollaboratorProfile();
        }
      } else {
        loadPublicProfile(profileId, { showOverlay });
      }

      return;
    }

    // For logged-in users, make API call
    const currentProfileId =
      collaborator?.currentProfile?._id ?? collaborator?.currentProfile ?? null;

    if (
      (profileId == null && collaborator.currentProfile === null) ||
      (profileId !== null &&
        currentProfileId != null &&
        String(profileId) === String(currentProfileId))
    ) {
      return;
    }

    patchCollaborator({
      id: collaborator._id,
      content: { currentProfile: profileId },
    })
      .unwrap()
      .catch((err) => {
        console.error("Error switching profile:", err);
      });
  };

  const handleToggleProfile = () => {
    const base = originalCollaboratorRef.current || data?.data?.collaborator;
    const publicProfiles = getPublicProfilesList(base);
    if (publicProfiles.length < 2) return;

    const currentId = getProfileEntryId(activePublicProfile);
    const nextProfile =
      publicProfiles.find(
        (profile) => String(getProfileEntryId(profile)) !== String(currentId),
      ) ||
      publicProfiles[1];

    handleSelectProfile(getProfileEntryId(nextProfile), { showOverlay: true });
  };

  // Remove meetings deep-link only when there is clearly no session.
  // While the profile query hydrates Redux, `logged` stays false briefly — do not strip `view`
  // in that window or refresh loses Mes rencontres.
  useEffect(() => {
    if (localStorage.getItem("token")) return;
    if (searchParams.get(MEETINGS_VIEW_QUERY_KEY) !== MEETINGS_VIEW_QUERY_VALUE)
      return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(MEETINGS_VIEW_QUERY_KEY);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // ############### DEV AREA
  // ######################
  // ########################################################

  // ########################################################
  // ######################
  // ############### END DEV AREA

  let collaborator_id;

  useEffect(() => {
    if (!id) return;
    const redirectedId = profileRedirectMap[`/${id}`]?.replace("/", "");
    if (redirectedId) {
      const qs = searchParams.toString();
      navigate(
        {
          pathname: `/profile/${redirectedId}`,
          ...(qs ? { search: `?${qs}` } : {}),
        },
        { replace: true },
      );
    }
  }, [id, navigate, searchParams]);

  useEffect(() => {
    if (data) {
      document.title = `Popcard - ${data?.data?.collaborator?.first_name} ${data.data.collaborator?.last_name}`;
      collaborator_id = data?.data?.collaborator?._id;
      const cleanProfilePicture = (
        data?.data?.collaborator?.profile_picture || ""
      )
        .toString()
        .trim()
        .replace(/^['"]+|['"]+$/g, "");
      sendTracking({
        collaborator_id: data.data.collaborator.collaborator_id,
        action: 51,
      });

      const metaElement = document.querySelector('meta[name="description"]');
      const linkElement = document.querySelector(
        'link[rel="apple-touch-icon"]',
      );
      linkElement.href = `${process.env.REACT_APP_IMAGE_PATH}${cleanProfilePicture}`;
      metaElement.content = `${data?.data?.collaborator?.first_name} ${data.data.collaborator?.last_name}`;
    }
  }, [data]);

  useLayoutEffect(() => {
    if (!data) return;

    const raw = data.data.collaborator;

    // Always keep unmerged collaborator data for public profile switching
    originalCollaboratorRef.current = {
      ...raw,
      _id: raw._id,
      backoffice_id: raw?.backoffice_id,
      team_id: raw?.team_id,
    };

    if (logged) {
      if (
        raw.profileDisplayMode === "both" &&
        raw.profiles?.length >= 2 &&
        !raw.currentProfile
      ) {
        dispatch(getCollaborator(withCollaboratorMeta(raw)));
      } else {
        dispatch(getCollaborator(buildCollaboratorState(raw)));
      }
      return;
    }

    if (raw.profileDisplayMode !== "both" || raw.profiles?.length < 2) {
      setActivePublicProfile(null);
      profileInitStartedRef.current = false;
      setProfileInitialized(true);
      dispatch(getCollaborator(buildCollaboratorState(raw)));
      return;
    }
  }, [data, dispatch, logged]);

  // Logged-in: first profile uses collaborator data; secondary uses GET /profiles/{id}
  useEffect(() => {
    if (!logged || !data?.data?.collaborator) return;

    const raw = originalCollaboratorRef.current || data.data.collaborator;
    if (!raw?.isMultiProfile) return;

    const currentProfileId =
      raw.currentProfile?._id ?? raw.currentProfile ?? null;

    if (!currentProfileId || isMainProfileId(raw, currentProfileId)) {
      dispatch(getCollaborator(withCollaboratorMeta(raw)));
      return;
    }

    let cancelled = false;

    fetchProfile(currentProfileId, false)
      .unwrap()
      .then((result) => {
        if (cancelled) return;
        const profile = result?.data?.profile;
        if (profile) {
          dispatch(getCollaborator(withCollaboratorMeta(raw, profile)));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Error loading active profile:", error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [logged, data?.data?.collaborator?.currentProfile, data, fetchProfile, dispatch]);

  const publicProfilesList = getPublicProfilesList(
    originalCollaboratorRef.current || data?.data?.collaborator,
  );

  const baseCollaborator =
    originalCollaboratorRef.current || data?.data?.collaborator;
  const showPublicProfileSwitcher =
    !logged &&
    shouldShowPublicProfileSwitcher(baseCollaborator, activePublicProfile);

  if (isLoading) {
    return <Loading />;
  }

  if (!data && !isLoading) {
    window.location.href = "https://www.getpopcard.com/";
  }

  if (data && !data.data.collaborator.active) {
    window.location.href = "https://www.getpopcard.com/";
  }

  const ProfileSections = [
    <ShowAbout />,
    <ShowInformation />,
    <ShowSocials />,
    <ShowProducts />,
    <ShowCalendly />,
    <ShowLinks />,
    <TestShowImages />,
    <ShowVideos />,
  ];

  return (
    <div
      dir={lang === "ar" ? "rtl" : "ltr"}
      className={`min-h-screen flex flex-col relative max-w-[580px] m-auto overflow-x-hidden ${
        lang === "ar" ? "!font-readexLight" : "font-SteradianRegular"
      }`}
    >
      {hideLogout && (
        <LogoutAnimation
          onHide={() => {
            setLogout(false);
            localStorage.setItem("logout", "false");
          }}
        />
      )}

      <MeetingsModal
        show={showMeetingsModal}
        onClose={closeMeetingsModal}
        lang={lang}
      />

      {/* Profile Switcher Bar - only for public visitors with multi-profile + both mode */}
      {showPublicProfileSwitcher && !loginModalOpen && (
          <ProfileSwitcherBar
            profiles={publicProfilesList}
            currentProfile={activePublicProfile}
            onSwitch={handleToggleProfile}
            isSwitching={isSwitchingProfile}
            isAtBottom={contactButtonsVisible}
          />
      )}

      {!logged && (
        <ProfileSwitchOverlay
          isOpen={isSwitchingProfile}
          profiles={publicProfilesList}
        />
      )}

      <main className="mx-[20px] mb-9 flex-1 z-[99]">
        <Header />
        <MainCard
          activePublicProfile={activePublicProfile}
          onLoginModalChange={setLoginModalOpen}
        />
        <AddToContactButton />
        {canUseMeetingsFeature && (
          <div className="flex gap-3 my-3">
            {/* <button
              type="button"
              onClick={openMeetingsModal}
              className="w-full rounded-md bg-black py-3.5 text-[15px] font-medium text-white transition-all duration-300 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            >
              {translation?.meetingsModal?.seeAllContacts?.[lang]}
            </button> */}
            {/* mu */}
          </div>
        )}
        <div id="show-btn" className="fixed w-full z-[9999]" />

        {!logged && <AddToContactButtonFloat />}

        {collaborator?.element_order?.map((order) => {
          return (
            <div key={order?.toString()}>{ProfileSections[order - 1]}</div>
          );
        })}
      </main>
      <Footer />
    </div>
  );
};

export default Profile;
